import { memo, useMemo, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { Icon } from "../../ui/Icon";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../../ui/Toast";
import { SearchSelect, type SSOption } from "../../ui/SearchSelect";
import { Drawer } from "../../Drawer";
import { useIsMobile } from "../../../lib/useIsMobile";
import { MCard } from "../../mobile/primitives";
import { WARN_SHORT, WARN_TEXT } from "../shiftWarn";
import { HardDeleteConfirm } from "../../ui/HardDeleteConfirm";
import "../../../pages/Items.css";       // dt2-wrap / dt2-toolbar / dt2-filter / empty-state / iact / badge-pill
import "../../../pages/Maintenance.css"; // mt-empty
import "../../../pages/Personnel.css";   // seg / hr-toolbar / hr-bulk* / hr-mfld / hr-listerr (İK katmanı)
import { ShiftPlanImportDrawer } from "../ShiftPlanImportDrawer";
import { ShiftWeekBoard } from "../ShiftWeekBoard";
import "./ShiftPlanTab.css";

/**
 * STEPERP — İK / Vardiya Planı sekmesi.
 *
 * Aylık ızgara: satır = personel, sütun = ayın günleri, hücre = o günün vardiyası.
 * Plan katmanları (sunucu çözer): elle istisna > rotasyon şablonu > personel varsayılanı.
 * "Ayı Yayınla" planı dondurur — sonradan şablon değişse bile o ay değişmez.
 *
 * PERFORMANS: 100 personel × 31 gün = 3.100 hücre. Üç önlem:
 *   1) Sunucu verisi TEK useMemo ile hazır `CellView[]`e çevrilir (sınıf/metin/tooltip önceden).
 *   2) Satır `memo(ShpRow)` — prop referansı o useMemo'dan gelir, drawer/toast satırları etkilemez.
 *   3) Hücrede onClick YOK; `<tbody>` delegasyonu `data-emp`/`data-date` okur (closure yok).
 *
 * İzin: `view:shift_plan` okuma, `manage:shift_plan` yazma (canManage).
 */

type PeriodStatus = "draft" | "published" | "locked";

const PERIOD_META: Record<PeriodStatus, { cls: string; icon: string; label: string }> = {
  draft: { cls: "b-warn", icon: "edit", label: "Taslak" },
  published: { cls: "b-success", icon: "checkCircle", label: "Yayınlandı" },
  locked: { cls: "b-info", icon: "lock", label: "Kilitli" },
};

/** Uyarı anahtarı → kullanıcı metni + ne yapmalı. Sunucudaki ShiftWarning birleşiminin tamamı. */
// WARN_SHORT / WARN_TEXT ORTAK modülden (haftalık pano da aynı sözlüğü kullanır).
/** Düzeltme yolu — her biri UYGULAMADA GERÇEKTEN VAR OLAN bir ekranı işaret etmeli. */
const WARN_FIX: Record<string, string> = {
  employee_missing: "Personel kartını kontrol edin (Personeller sekmesi).",
  no_assignment: "\"Toplu Ata\" ile rotasyon şablonu atayın; ya da personel satırına tıklayıp \"Varsayılan vardiya\" seçin.",
  pattern_missing: "Personel satırına tıklayıp eski atamayı kaldırın, ardından \"Toplu Ata\" ile yenisini verin.",
  pattern_gap: "\"Şablonlar\"dan şablonu açıp eksik slotları doldurun (döngü uzunluğu kadar slot olmalı).",
  shift_inactive: "Ayarlar > Çalışma Düzeni'nden vardiyayı yeniden aktif edin veya şablonu güncelleyin.",
  assignment_overlap: "Personel satırına tıklayıp \"Rotasyon atamaları\" listesinden çakışan atamayı kaldırın.",
  pattern_tesis_mismatch: "Personelin tesisine ait bir şablon atayın.",
};

/** Vardiya tonları — chart-2 (siyah) ve chart-8 (soluk) bilinçli atlandı. */
const SHIFT_TONES = ["var(--chart-1)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)", "var(--chart-6)", "var(--chart-7)"];

const p2 = (n: number) => String(n).padStart(2, "0");
/** YEREL bugün, "YYYY-MM-DD". `toISOString()` UTC'ye çevirir: TR'de 00:00-03:00 arası bir gün geri atar. */
const todayLocalIso = () => { const d = new Date(); return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`; };

const DOW = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
function dayLabel(day: string): { d: string; dow: string; dowIdx: number; long: string } {
  const [y, m, dd] = day.split("-").map(Number);
  const dt = new Date(y!, m! - 1, dd!);
  // TR biçimi sıfır dolgulu: "01.07.2026" (STEPERP kuralı).
  return { d: String(dt.getDate()), dow: DOW[dt.getDay()]!, dowIdx: dt.getDay(), long: `${p2(dt.getDate())}.${p2(dt.getMonth() + 1)}.${dt.getFullYear()}` };
}
/** Vardiya adından ızgaraya sığan kısaltma ("1. Vardiya" → "1V", "Sabah" → "SAB"). */
function shortOfName(name: string): string {
  const digit = name.match(/(\d+)\s*\.?\s*vardiya/i);
  if (digit) return `${digit[1]}V`;
  return name.replace(/[^\p{L}\p{N}]/gu, "").slice(0, 3).toLocaleUpperCase("tr-TR");
}
interface CellView { date: string; cls: string; text: string; tone: string | null; manual: boolean; bang: boolean; tip: string }
interface RowView { id: string; name: string; sub: string; workDays: number; warnDays: number; cells: CellView[] }

/** Satır — prop referansı sabit kaldığı sürece asla yeniden render olmaz. */
const ShpRow = memo(function ShpRow({ r }: { r: RowView }) {
  return (
    <tr>
      <td className="shp-name">
        <button type="button" className="shp-namebtn" data-emp-open={r.id} title={`${r.name} — ay detayını aç`}>
          <span className="shp-av" aria-hidden="true">{r.name.charAt(0).toLocaleUpperCase("tr-TR")}</span>
          <span className="shp-nametxt">{r.name}<small>{r.sub}</small></span>
          {r.warnDays > 0 ? (
            <span className="shp-warntag" title={`${r.warnDays} günde uyarı var`}>
              <Icon name="alertTriangle" size={10} />{r.warnDays}
            </span>
          ) : null}
        </button>
      </td>
      {r.cells.map((c) => (
        // aria-label: 3.100 hücre etiketsiz "tablo hücresi" diye okunuyordu; `tip` zaten hazır.
        <td key={c.date} className={c.cls} title={c.tip} aria-label={c.tip} data-emp={r.id} data-date={c.date}
          style={c.tone ? ({ ["--shp-c" as string]: c.tone } as React.CSSProperties) : undefined}>
          {c.bang ? <i className="shp-mark" aria-hidden="true">!</i> : null}
          {c.manual ? <Icon name="edit" size={9} /> : null}
          <span className="mono">{c.text}</span>
        </td>
      ))}
      <td className="shp-tot mono">{r.workDays}</td>
    </tr>
  );
});

export function ShiftPlanTab({ canManage, isSuper }: { canManage: boolean; isSuper: boolean }) {
  const qc = trpc.useUtils();
  const isMobile = useIsMobile();

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) => setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  const now = new Date();
  const [ym, setYmRaw] = useState(`${now.getFullYear()}-${p2(now.getMonth() + 1)}`);
  // Ay değişince açık uyarı açıklaması bayat kalıyordu (çipi kaybolsa bile panel duruyordu).
  const setYm = (v: string) => { setYmRaw(v); setOpenWarn(null); };
  const monthValid = /^\d{4}-\d{2}$/.test(ym);
  const year = monthValid ? Number(ym.slice(0, 4)) : now.getFullYear();
  const month = monthValid ? Number(ym.slice(5, 7)) : now.getMonth() + 1;

  const [openWarn, setOpenWarn] = useState<string | null>(null);
  const [exc, setExc] = useState<{ employeeId: string; date: string } | null>(null);
  const [monthDrawer, setMonthDrawer] = useState<string | null>(null);
  const [patternsOpen, setPatternsOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Varsayilan HAFTALIK (kullanici karari); aylik cizelge oldugu gibi duruyor.
  const [view, setView] = useState<"hafta" | "ay">("hafta");
  const [weekStart, setWeekStart] = useState(() => new Date().toISOString().slice(0, 10));

  // Dönem (yayınla/kilitle) TESİS BAZLIDIR. Süper-admin "Tüm Tesisler" modundayken ızgara
  // birden çok tesisin personelini birleştirir; o modda gösterilecek tek bir dönem YOKTUR
  // (findFirst rastgele birini döner) ve `stampMasterTesis` null'a düşüp "kapsamda personel
  // yok" hatası verir. Bu yüzden dönem şeridi ve yayınla/kilitle o modda kapatılır.
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const singleFacility = !!meQ.data?.user?.activeFacilityId;

  const gridQ = trpc.hr.shiftPlan.grid.useQuery({ year, month }, { enabled: monthValid, staleTime: 5_000 });
  const data = gridQ.data;
  const period = singleFacility ? (data?.period ?? null) : null;
  const locked = period?.status === "locked";
  const todayStr = `${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())}`;

  // ── TEK dönüşüm: sunucu verisi → hazır görünüm modeli (render döngüsünde hesap yok) ──
  const { rowViews, days, dayTotals, grandWorkDays, shiftMaps } = useMemo(() => {
    const empty = { rowViews: [] as RowView[], days: [] as string[], dayTotals: [] as Array<{ date: string; n: number }>, grandWorkDays: 0, shiftMaps: { short: new Map<string, string>(), name: new Map<string, string>(), plain: new Map<string, string>(), time: new Map<string, string>(), tone: new Map<string, string>() } };
    if (!data) return empty;

    const short = new Map<string, string>(), nameOf = new Map<string, string>(), tone = new Map<string, string>();
    const plain = new Map<string, string>(), time = new Map<string, string>();
    data.shifts.forEach((s, i) => {
      short.set(s.id, shortOfName(s.name));
      nameOf.set(s.id, `${s.name} (${s.startTime}–${s.endTime})`);
      plain.set(s.id, s.name);
      time.set(s.id, `${s.startTime}–${s.endTime}`);
      tone.set(s.id, SHIFT_TONES[i % SHIFT_TONES.length]!);
    });

    const empById = new Map(data.employees.map((e) => [e.id, e]));
    const dayList = data.rows[0]?.days.map((d) => d.date) ?? [];
    const totals = new Map<string, number>(dayList.map((d) => [d, 0]));
    let grand = 0;

    const rows: RowView[] = data.rows.map((row) => {
      const emp = empById.get(row.employeeId);
      const name = emp?.name ?? "—";
      let workDays = 0, warnDays = 0;
      const cells: CellView[] = row.days.map((d) => {
        const l = dayLabel(d.date);
        const we = l.dowIdx === 0 || l.dowIdx === 6;
        const parts = [`${name} · ${l.long} ${l.dow}`];
        let text = "—", kindCls = "off";
        let toneVar: string | null = null;

        if (d.kind === "work" && d.shiftId) {
          kindCls = "work"; text = short.get(d.shiftId) ?? "?";
          toneVar = tone.get(d.shiftId) ?? "var(--primary)";
          parts.push(nameOf.get(d.shiftId) ?? "Vardiya");
          workDays++; totals.set(d.date, (totals.get(d.date) ?? 0) + 1); grand++;
        } else if (d.source === "calendar_off") {
          kindCls = "hol"; text = "T"; parts.push("Tatil / hafta tatili");
        } else if (d.source === "pre_hire") {
          kindCls = "out"; text = "·"; parts.push("İşe giriş tarihinden önce");
        } else if (d.source === "post_termination") {
          kindCls = "out"; text = "·"; parts.push("Ayrılış tarihinden sonra");
        } else if (d.source === "unassigned") {
          kindCls = "none"; text = "?"; parts.push("Plan yok");
        } else {
          parts.push("İstirahat");
        }

        const manual = d.source === "manual";
        if (manual) parts.push(d.reason ? `Elle istisna: ${d.reason}` : "Elle istisna");
        if (d.warnings.length) { warnDays++; parts.push(...d.warnings.map((w) => WARN_TEXT[w] ?? w)); }

        const cls = [
          "shp-cell", kindCls,
          we ? "we" : "", d.date === todayStr ? "today" : "",
          manual ? "man" : "", canManage && !locked ? "is-edit" : "",
        ].filter(Boolean).join(" ");
        return { date: d.date, cls, text, tone: toneVar, manual, bang: d.warnings.length > 0, tip: parts.join(" · ") };
      });
      return { id: row.employeeId, name, sub: emp?.positionTitle ?? emp?.code ?? "", workDays, warnDays, cells };
    });

    return {
      rowViews: rows, days: dayList,
      dayTotals: dayList.map((d) => ({ date: d, n: totals.get(d) ?? 0 })),
      grandWorkDays: grand,
      shiftMaps: { short, name: nameOf, plain, time, tone },
    };
  }, [data, todayStr, canManage, locked]);

  /**
   * HIZLI FİLTRE — ad veya pozisyona göre satır süzme (yalnız görüntü; sunucuya gitmez).
   * Türkçe katlama ZORUNLU: "İrem".toLowerCase() birleşik noktalı "i̇rem" üretir ve
   * "irem" ile EŞLEŞMEZ. SearchSelect'teki kuralın aynısı.
   */
  const foldTr = (s: string) =>
    s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/ı/g, "i");
  const [nameFilter, setNameFilter] = useState("");
  const [clearOpen, setClearOpen] = useState(false);
  const clearMut = trpc.hr.shiftPlan.planClear.useMutation({
    onSuccess: (r) => {
      setClearOpen(false);
      void qc.hr.shiftPlan.invalidate();
      pushToast({ tone: "success", title: "Ay temizlendi", message: `${r.deleted} gün satırı silindi. Ay artık şablondan hesaplanıyor.` });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Temizlenemedi", message: e.message }),
  });
  const visibleRows = useMemo(() => {
    const q = foldTr(nameFilter.trim());
    if (!q) return rowViews;
    return rowViews.filter((r) => foldTr(`${r.name} ${r.sub}`).includes(q));
  }, [rowViews, nameFilter]);
  const filtering = nameFilter.trim().length > 0;

  const warnEntries = Object.entries(data?.warningSummary ?? {}).filter(([, n]) => (n as number) > 0) as Array<[string, number]>;
  /**
   * Tesissiz (global) personel ızgarada GÖRÜNÜR — `masterFacilityFilter` tesisId=null satırları
   * da alır — ama `periodPublish` KATI eşitlik kullandığı için onların ayı asla dondurulmaz ve
   * hücrelerine yazma FORBIDDEN döner. Sessiz kalırsa "yayınlandı" yanıltıcı olur.
   */
  const globalEmps = (data?.employees ?? []).filter((e) => e.tesisId == null);

  // ── Mutasyonlar ──
  const invalidate = () => qc.hr.shiftPlan.invalidate();
  const publishMut = trpc.hr.shiftPlan.periodPublish.useMutation({
    onSuccess: (r) => {
      invalidate();
      pushToast({
        tone: "success", title: `${r.written} gün satırı yazıldı`,
        message: `${r.employeeCount} personel · ayda toplam ${r.dayCount} satır`,
        lines: Object.entries(r.warningSummary).map(([k, n]) => `${WARN_TEXT[k] ?? k}: ${n} hücre`),
      });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Yayınlanamadı", message: e.message }),
  });
  const lockMut = trpc.hr.shiftPlan.periodLock.useMutation({
    onSuccess: () => { invalidate(); pushToast({ tone: "success", message: "Ay kilitlendi." }); },
    onError: (e) => pushToast({ tone: "danger", title: "Kilitlenemedi", message: e.message }),
  });
  const reopenMut = trpc.hr.shiftPlan.periodReopen.useMutation({
    onSuccess: () => { invalidate(); pushToast({ tone: "success", message: "Kilit açıldı." }); },
    onError: (e) => pushToast({ tone: "danger", title: "Kilit açılamadı", message: e.message }),
  });

  /** Hücre tıklaması — delegasyon. Ad butonu ÖNCE kontrol edilir (çakışma yok). */
  const onGridClick = (e: React.MouseEvent<HTMLTableSectionElement>) => {
    const t = e.target as HTMLElement;
    const nameBtn = t.closest("button[data-emp-open]") as HTMLButtonElement | null;
    if (nameBtn) { setMonthDrawer(nameBtn.dataset.empOpen!); return; }
    const td = t.closest("td[data-emp]") as HTMLTableCellElement | null;
    if (!td || !canManage) return;
    if (locked) {
      pushToast({ tone: "warning", title: "Ay kilitli", message: "Bu ayın vardiya planı kilitli — değiştirmek için önce kilidi açın." });
      return;
    }
    setExc({ employeeId: td.dataset.emp!, date: td.dataset.date! });
  };

  const excDay = useMemo(() => {
    if (!exc || !data) return null;
    const row = data.rows.find((r) => r.employeeId === exc.employeeId);
    return row?.days.find((d) => d.date === exc.date) ?? null;
  }, [exc, data]);

  const drawers = (
    <>
      {exc && excDay ? (
        <DayExceptionDrawer
          key={`${exc.employeeId}|${exc.date}`}
          employeeId={exc.employeeId}
          employeeName={data?.employees.find((e) => e.id === exc.employeeId)?.name ?? ""}
          date={exc.date}
          current={excDay}
          shifts={data?.shifts ?? []}
          published={!!period}
          onClose={() => setExc(null)}
          onDone={() => { invalidate(); setExc(null); }}
          pushToast={pushToast}
        />
      ) : null}

      {monthDrawer && data ? (
        <MonthDetailDrawer
          key={monthDrawer}
          employeeId={monthDrawer}
          employeeName={data.employees.find((e) => e.id === monthDrawer)?.name ?? ""}
          employeeDefaultShiftId={data.employees.find((e) => e.id === monthDrawer)?.defaultShiftId ?? null}
          days={data.rows.find((r) => r.employeeId === monthDrawer)?.days ?? []}
          shiftName={shiftMaps.plain}
          shiftTime={shiftMaps.time}
          shifts={data.shifts}
          canManage={canManage && !locked}
          onClose={() => setMonthDrawer(null)}
          onEditDay={(date) => { setMonthDrawer(null); setExc({ employeeId: monthDrawer, date }); }}
          pushToast={pushToast}
        />
      ) : null}

      {canManage && patternsOpen ? (
        <PatternsDrawer shifts={data?.shifts ?? []} onClose={() => setPatternsOpen(false)} pushToast={pushToast} />
      ) : null}

      {canManage && importOpen ? (
        <ShiftPlanImportDrawer open year={year} month={month} onClose={() => { setImportOpen(false); invalidate(); }} />
      ) : null}

      {canManage && bulkOpen ? (
        <BulkAssignDrawer
          employees={data?.employees ?? []}
          onClose={() => setBulkOpen(false)}
          onDone={() => { invalidate(); setBulkOpen(false); }}
          pushToast={pushToast}
        />
      ) : null}

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );

  // ── Ortak üst şerit parçaları ──
  const st: PeriodStatus = (period?.status as PeriodStatus) ?? "draft";
  const pm = PERIOD_META[st];

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          {canManage ? (
            <div className="m-actions">
              {/* monthValid guard masaüstüyle AYNI: geçersiz ay'da year/month bugüne düşüyor,
                  buton boş ekranda gerçek içinde bulunulan ayı yayınlıyordu. */}
              {singleFacility && !locked ? (
                <button type="button" className="btn-md primary sm" disabled={publishMut.isPending || !monthValid}
                  onClick={() => publishMut.mutate({ year, month })}>
                  <Icon name="upload" size={14} />Ayı Yayınla
                </button>
              ) : null}
              {period && st === "published" ? (
                <button type="button" className="btn-md secondary sm" disabled={lockMut.isPending} onClick={() => lockMut.mutate({ id: period.id })}>
                  <Icon name="lock" size={14} />Kilitle
                </button>
              ) : null}
              {period && locked && isSuper ? (
                <button type="button" className="btn-md secondary sm" disabled={reopenMut.isPending} onClick={() => reopenMut.mutate({ id: period.id })}>
                  <Icon name="lock" size={14} />Kilidi Aç
                </button>
              ) : null}
              <button type="button" className="btn-md secondary sm" onClick={() => setBulkOpen(true)}>
                <Icon name="users" size={14} />Toplu Ata
              </button>
              <button type="button" className="btn-md secondary sm" onClick={() => setPatternsOpen(true)}>
                <Icon name="list2" size={14} />Şablonlar
              </button>
              <button type="button" className="btn-md secondary sm" onClick={() => setImportOpen(true)}>
                <Icon name="sheet" size={14} />Excel Aktarım
              </button>
            </div>
          ) : null}
          <label className="hr-mfld">
            <span className="m-ovl">Ay</span>
            <input className="form-input" type="month" value={ym} onChange={(e) => setYm(e.target.value)} />
          </label>
        </div>

        <MCard pad>
          <div className="shp-mhead">
            {singleFacility ? <span className={`badge-pill ${pm.cls}`}><Icon name={pm.icon} size={11} />{pm.label}</span> : null}
            <span className="shp-mmeta">{rowViews.length} personel · {grandWorkDays} çalışma günü</span>
            {warnEntries.length ? (
              <span className="shp-warntag"><Icon name="alertTriangle" size={10} />{warnEntries.reduce((a, [, n]) => a + n, 0)} uyarı</span>
            ) : null}
          </div>
        </MCard>

        {gridQ.isLoading ? <div className="m-empty">Yükleniyor…</div>
          : gridQ.isError ? <div className="m-empty">Plan yüklenemedi: {gridQ.error.message}</div>
          : rowViews.length === 0 ? <div className="m-empty">Bu ay için kapsamda personel yok.</div>
          : rowViews.map((r) => (
            <MCard pad key={r.id} className="shp-mrow" onClick={() => setMonthDrawer(r.id)}>
              <div className="shp-mtop">
                <span className="shp-mname">
                  <b>{r.name}</b>
                  <small>{r.sub}</small>
                </span>
                {r.warnDays > 0 ? <span className="shp-warntag"><Icon name="alertTriangle" size={10} />{r.warnDays}</span> : null}
                <Icon name="chevRight" size={16} />
              </div>
              <div className="shp-mmeta"><b className="mono">{r.workDays}</b> çalışma günü</div>
            </MCard>
          ))}
        {drawers}
      </>
    );
  }

  return (
    <div className="dt2-wrap">
      <div className="dt2-toolbar hr-toolbar">
        {/* Varsayılan HAFTALIK: sahadaki düzen haftalık rotasyon; aylık çizelge kontrol/denetim için kalıyor. */}
        <div className="seg" role="group" aria-label="Görünüm">
          <button type="button" className={`seg-btn ${view === "hafta" ? "active" : ""}`} onClick={() => setView("hafta")}>
            <Icon name="columns" size={14} />Haftalık
          </button>
          <button type="button" className={`seg-btn ${view === "ay" ? "active" : ""}`} onClick={() => setView("ay")}>
            <Icon name="calendar" size={14} />Aylık
          </button>
        </div>
        {view === "ay" ? (
          <div className="dt2-filter">
            <input className="form-input" type="month" value={ym} aria-label="Ay" onChange={(e) => setYm(e.target.value)} />
          </div>
        ) : null}
        <span className="grow" />
        {canManage ? (
          <>
            <button type="button" className="btn-md ghost" onClick={() => setPatternsOpen(true)}>
              <Icon name="list2" size={14} />Şablonlar
            </button>
            <button type="button" className="btn-md secondary" onClick={() => setBulkOpen(true)}>
              <Icon name="users" size={14} />Toplu Ata
            </button>
            <button type="button" className="btn-md secondary" onClick={() => setImportOpen(true)}>
              <Icon name="sheet" size={14} />Excel Aktarım
            </button>
            {/* AY kapsamlı aksiyonlar YALNIZ aylık görünümde. Haftalık görünümde ay seçici ve
                ay durum rozeti çizilmediği için bu butonlar hedefledikleri ayı GÖSTERMEDEN
                çalışırdı: kullanıcı Ağustos haftasındayken "Ayı Yayınla" Temmuz'u yayınlar,
                "Kilitle" Temmuz'u kilitler (kilidi yalnız süper-admin açabilir). Üstelik
                haftalık panonun kendi "Yayınla/Kilitle" butonları hemen altta duruyor —
                aynı ekranda aynı etiketli iki farklı kapsam. */}
            {view === "ay" ? (
              <>
                {!locked ? (
                  <button type="button" className="btn-md ghost" disabled={clearMut.isPending || !monthValid}
                    onClick={() => { clearMut.reset(); setClearOpen(true); }}
                    title="Bu ayın yazılı planını sil; ay yeniden şablondan hesaplanır">
                    <Icon name="trash" size={14} />Ayı Temizle
                  </button>
                ) : null}
                {singleFacility && !locked ? (
                  <button type="button" className="btn-md primary" disabled={publishMut.isPending || !monthValid}
                    onClick={() => publishMut.mutate({ year, month })}>
                    <Icon name="upload" size={14} />{publishMut.isPending ? "Yayınlanıyor…" : "Ayı Yayınla"}
                  </button>
                ) : null}
                {period && st === "published" ? (
                  <button type="button" className="btn-md secondary" disabled={lockMut.isPending} onClick={() => lockMut.mutate({ id: period.id })}>
                    <Icon name="lock" size={14} />Ayı Kilitle
                  </button>
                ) : null}
                {period && locked && isSuper ? (
                  <button type="button" className="btn-md secondary" disabled={reopenMut.isPending} onClick={() => reopenMut.mutate({ id: period.id })}>
                    <Icon name="lock" size={14} />Ayın Kilidini Aç
                  </button>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}
      </div>

      {/* AYA ait şerit ve bantlar YALNIZ aylık görünümde. Haftalık panonun kendi durum
          rozeti ve kendi kilit bantları var; ikisini birden çizmek hem çelişkili iki durum
          gösteriyor hem de tablodan ~150px dikey alan çalıyordu. Tesis kapsamı uyarıları
          ise İKİ görünümde de geçerli (haftayı yerleştirme de tesis bazlı) → korunur. */}
      {view === "ay" ? (
      <>
      <div className="shp-bar">
        {singleFacility ? <span className={`badge-pill ${pm.cls}`}><Icon name={pm.icon} size={11} />{pm.label}</span> : null}
        <span className="shp-meta" role="status" aria-live="polite"><b>{rowViews.length}</b> personel · <b>{days.length}</b> gün · <b>{grandWorkDays}</b> çalışma günü</span>
        <span className="grow" />
        {warnEntries.map(([k, n]) => (
          <button key={k} type="button" className="shp-warn" aria-expanded={openWarn === k} onClick={() => setOpenWarn(openWarn === k ? null : k)}>
            <Icon name="alertTriangle" size={11} />{n} hücre — {WARN_SHORT[k] ?? k}
          </button>
        ))}
      </div>
      {openWarn ? (
        <div className="shp-warnlist">
          <b>{WARN_TEXT[openWarn] ?? openWarn}</b>
          <p className="shp-warnhint">{WARN_FIX[openWarn] ?? ""}</p>
        </div>
      ) : null}
      </>
      ) : null}
      {!singleFacility ? (
        <div className="form-banner-info hr-listerr">
          <Icon name="info" size={14} className="hr-bnr-ic" />
          <div><b>Tüm Tesisler</b> görünümü — ızgara birleşik. Ay yayınlama ve kilitleme tesis bazlıdır; üst çubuktan bir tesis seçin.</div>
        </div>
      ) : view === "ay" && !period ? (
        <div className="form-banner-info hr-listerr">
          <Icon name="info" size={14} className="hr-bnr-ic" />
          <div>Bu ay henüz yayınlanmadı — ızgara canlı hesaplanıyor. Şablon veya atama değişirse geçmiş günler de değişir. Kesinleştirmek için <b>Ayı Yayınla</b> deyin.</div>
        </div>
      ) : null}
      {view === "ay" && locked ? (
        <div className="form-banner-info hr-listerr">
          <Icon name="info" size={14} className="hr-bnr-ic" />
          <div>Bu ay <b>kilitli</b> — hücre düzenleme, yayınlama ve istisna kaldırma kapalı.</div>
        </div>
      ) : null}
      {globalEmps.length ? (
        <div className="form-banner-warn hr-listerr" role="alert">
          <Icon name="alertTriangle" size={14} className="hr-bnr-ic" />
          <div>
            <b>{globalEmps.length} personel hiçbir tesise bağlı değil</b> ({globalEmps.slice(0, 5).map((e) => e.name).join(", ")}
            {globalEmps.length > 5 ? " …" : ""}). Izgarada görünürler ama ay yayınlamaya <b>dâhil edilmezler</b> ve
            günleri düzenlenemez. Personeller sekmesinden tesis atayın.
          </div>
        </div>
      ) : null}

      {view === "hafta" ? (
        <ShiftWeekBoard weekStart={weekStart} onWeekChange={setWeekStart} canManage={canManage} />
      ) : (
      <>
      <div className="shp-legend">
        <span className="shp-lg"><span className="shp-sw work">1V</span>Vardiya</span>
        <span className="shp-lg"><span className="shp-sw">—</span>İstirahat</span>
        <span className="shp-lg"><span className="shp-sw hol">T</span>Tatil</span>
        <span className="shp-lg"><span className="shp-sw none">?</span>Plan yok</span>
        <span className="shp-lg"><span className="shp-sw man"><Icon name="edit" size={9} /></span>Elle istisna</span>
        <span className="shp-lg"><span className="shp-sw">·</span>Kapsam dışı</span>
      </div>

      {!monthValid ? (
        <div className="empty-state">
          <span className="ic" aria-hidden="true"><Icon name="calendar" size={30} /></span>
          <p className="ttl">Ay seçin</p>
          <p className="sub">Vardiya planını görmek için geçerli bir ay belirleyin.</p>
        </div>
      ) : gridQ.isLoading ? (
        <div className="mt-empty">Yükleniyor…</div>
      ) : gridQ.isError ? (
        <div className="form-banner-err hr-listerr">Vardiya planı yüklenemedi: {gridQ.error.message}</div>
      ) : rowViews.length === 0 ? (
        <div className="empty-state">
          <span className="ic" aria-hidden="true"><Icon name="users" size={30} /></span>
          <p className="ttl">Kapsamda personel yok</p>
          <p className="sub">Bu tesiste bu ay için aktif personel bulunamadı.</p>
        </div>
      ) : (
        // tabIndex=0: yatay kaydırılan bölge klavyeyle de gezilebilsin (ok tuşları).
        // Hücre düzenlemenin klavye yolu ayrıdır: personel adı butonu → ay dökümü → gün düzenle.
        <div className="shp-scroll" tabIndex={0} role="region" aria-label="Aylık vardiya ızgarası">
          <table className="shp-table">
            <thead>
              <tr>
                <th className="shp-name-h">
                  <span className="shp-name-lbl">Personel</span>
                  <input
                    type="search" className="spl-find" value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setNameFilter(""); } }}
                    placeholder="Ada göre süz…" aria-label="Personel listesini ada göre süz"
                  />
                </th>
                {days.map((d) => {
                  const l = dayLabel(d);
                  const we = l.dowIdx === 0 || l.dowIdx === 6;
                  return (
                    <th key={d} className={`shp-day-h${we ? " we" : ""}${d === todayStr ? " today" : ""}`} title={l.long}>
                      <span className="shp-dow">{l.dow}</span>
                      <span className="mono">{l.d}</span>
                    </th>
                  );
                })}
                <th className="shp-tot-h" title="Ay içindeki çalışma günü sayısı">Çal.</th>
              </tr>
            </thead>
            <tbody onClick={onGridClick}>
              {visibleRows.length === 0 ? (
                <tr><td className="shp-name" colSpan={days.length + 2}>“{nameFilter}” ile eşleşen personel yok.</td></tr>
              ) : visibleRows.map((r) => <ShpRow key={r.id} r={r} />)}
              {/* Toplam satırı AYIN TAMAMINI anlatır; süzme açıkken 3 satır gösterip
                  560 çalışma günü yazmak yanıltıcı olurdu → süzerken gizlenir. */}
              {filtering ? null : (
                <tr className="spl-sum">
                  <td className="shp-name">Gün toplamı</td>
                  {dayTotals.map((t) => <td key={t.date} className="shp-cell off"><span className="mono">{t.n || ""}</span></td>)}
                  <td className="shp-tot mono">{grandWorkDays}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="dt2-foot">
        <span>{gridQ.isLoading ? "Yükleniyor…"
          : filtering ? `${visibleRows.length} / ${rowViews.length} personel gösteriliyor · ${days.length} gün`
          : `${rowViews.length} personel · ${days.length} gün`}</span>
      </div>
      </>
      )}

      {drawers}

      {/* YIKICI: elle konulmuş / Excel'den gelen günler de silinir → çift onay. */}
      <HardDeleteConfirm
        open={clearOpen}
        code="TEMİZLE"
        title="Ayın yazılı planını temizle"
        warning={
          <>
            <b>{ym}</b> ayı için yazılmış TÜM gün satırları silinecek — şablondan gelenler
            <b> ve elle konulmuş / Excel'den aktarılmış olanlar dâhil</b>.
            <br /><br />
            Ay bundan sonra şablondan <b>canlı hesaplanır</b>: plan kaybolmaz, yeniden üretilir.
            Ama elle girdiğiniz istisnalar <b>geri alınamaz</b>.
            <br /><br />
            Puantaj ve bordro kayıtları bu işlemden etkilenmez.
          </>
        }
        busy={clearMut.isPending}
        errorMessage={clearMut.error?.message ?? null}
        onConfirmed={() => clearMut.mutate({ scope: "month", date: `${ym}-01` })}
        onClose={() => { clearMut.reset(); setClearOpen(false); }}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Gün istisnası
// ════════════════════════════════════════════════════════════════════════════

interface DayInfo { date: string; shiftId: string | null; kind: "work" | "off"; source: string; frozen: boolean; reason: string | null; warnings: string[] }

function DayExceptionDrawer({
  employeeId, employeeName, date, current, shifts, published, onClose, onDone, pushToast,
}: {
  employeeId: string; employeeName: string; date: string; current: DayInfo;
  shifts: Array<{ id: string; name: string; startTime: string; endTime: string; isActive: boolean }>;
  published: boolean;
  onClose: () => void; onDone: () => void; pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const [kind, setKind] = useState<"work" | "off">(current.kind);
  const [shiftId, setShiftId] = useState<string | null>(current.shiftId);
  // Mevcut gerekçe ÖN-DOLDURULUR: boş açılırsa ikinci kayıtta sessizce silinirdi.
  const [reason, setReason] = useState(current.reason ?? "");
  const [delOpen, setDelOpen] = useState(false);
  const l = dayLabel(date);

  const setMut = trpc.hr.shiftPlan.daySet.useMutation({
    onSuccess: () => { pushToast({ tone: "success", message: "Gün planı güncellendi." }); onDone(); },
    onError: (e) => pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message }),
  });
  const clearMut = trpc.hr.shiftPlan.dayClear.useMutation({
    onSuccess: (r) => {
      // Yayınlanmış ayda istisna silinince O GÜN dondurulmuş satırını kaybeder ve canlı
      // hesaba döner — ay "yayınlandı" görünse de o gün şablon değişiminden etkilenir.
      pushToast({
        tone: r.removed && published ? "warning" : "success",
        message: r.removed
          ? (published
              ? "İstisna kaldırıldı — bu gün artık canlı hesaplanıyor. Dondurmak için ayı yeniden yayınlayın."
              : "İstisna kaldırıldı.")
          : "Bu günde zaten istisna yoktu.",
      });
      setDelOpen(false); onDone();
    },
    onError: (e) => { setDelOpen(false); pushToast({ tone: "danger", title: "Kaldırılamadı", message: e.message }); },
  });

  const opts = useMemo<SSOption[]>(
    () => shifts.filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.name, tail: `${s.startTime}–${s.endTime}` })),
    [shifts],
  );
  const valid = kind === "off" || !!shiftId;

  return (
    <Drawer
      open onClose={onClose} size="sm" dismissible={false}
      title={`${employeeName} — ${l.long} ${l.dow}`}
      subtitle="Bu güne özel vardiya ataması"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={setMut.isPending}>Vazgeç</button>
          <button type="button" className="btn-md primary" disabled={!valid || setMut.isPending}
            onClick={() => setMut.mutate({ employeeId, date, kind, shiftId: kind === "work" ? shiftId : null, reason: reason.trim() || null })}>
            {setMut.isPending ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </>
      }
    >
      <div className="shp-cur">
        <span className="badge-pill">{current.source === "manual" ? "Elle istisna" : current.source === "rotation" ? "Rotasyondan" : current.source === "employee_default" ? "Varsayılan vardiya" : current.source === "calendar_off" ? "Takvim tatili" : "Plan yok"}</span>
        <span>{current.shiftId ? (shifts.find((s) => s.id === current.shiftId)?.name ?? "Vardiya") : "İstirahat"}</span>
        {current.warnings.length ? <span className="shp-warntag"><Icon name="alertTriangle" size={10} />{current.warnings.map((w) => WARN_SHORT[w] ?? w).join(", ")}</span> : null}
        {/* Kaldırma AKSİYONU footer'da değil burada: mevcut istisnaya bağlamsal ve
            footer'da 3. buton "İstisnayı Kaldır"ı iki satıra kırıyordu. */}
        {current.source === "manual" ? (
          <>
            <span className="grow" />
            <button type="button" className="shp-linkbtn danger" onClick={() => setDelOpen(true)} disabled={clearMut.isPending}>
              <Icon name="trash" size={12} />İstisnayı kaldır
            </button>
          </>
        ) : null}
      </div>

      <div className="form-field">
        <span className="lbl">Gün tipi</span>
        <div className="seg shp-seg">
          <button type="button" className={`seg-btn${kind === "work" ? " active" : ""}`} onClick={() => setKind("work")}>Çalışma</button>
          <button type="button" className={`seg-btn${kind === "off" ? " active" : ""}`} onClick={() => { setKind("off"); setShiftId(null); }}>İstirahat</button>
        </div>
      </div>

      {kind === "work" ? (
        <div className="form-field">
          <span className="lbl">Vardiya</span>
          <SearchSelect value={shiftId} options={opts} onChange={setShiftId} placeholder="Vardiya seçin…" searchPlaceholder="Vardiya ara…" emptyText="Aktif vardiya yok" />
        </div>
      ) : null}

      <div className="form-field">
        <label className="lbl" htmlFor="shp-reason">Gerekçe</label>
        <textarea id="shp-reason" className="form-textarea" rows={3} maxLength={500} value={reason}
          onChange={(e) => setReason(e.target.value)} placeholder="Örn. hat arızası — gece vardiyasına kaydırıldı" />
        <span className="help">İsteğe bağlı; plan denetiminde bu not görünür.</span>
      </div>

      <ConfirmDialog
        open={delOpen}
        title="İstisna kaldırılsın mı?"
        message="Bu gün yeniden rotasyon planına döner. İşlem geri alınabilir — istisnayı tekrar girebilirsiniz."
        tone="danger" confirmLabel="Kaldır" cancelLabel="Vazgeç" busy={clearMut.isPending}
        onCancel={() => setDelOpen(false)}
        onConfirm={() => clearMut.mutate({ employeeId, date })}
      />
    </Drawer>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Ay detayı (klavye yolu + mobil ana yol)
// ════════════════════════════════════════════════════════════════════════════

function MonthDetailDrawer({
  employeeId, employeeName, employeeDefaultShiftId, days, shiftName, shiftTime, shifts, canManage, onClose, onEditDay, pushToast,
}: {
  employeeId: string; employeeName: string; employeeDefaultShiftId: string | null;
  days: DayInfo[]; shiftName: Map<string, string>; shiftTime: Map<string, string>;
  shifts: Array<{ id: string; name: string; startTime: string; endTime: string; isActive: boolean }>;
  canManage: boolean; onClose: () => void; onEditDay: (date: string) => void;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const SOURCE_LABEL: Record<string, string> = {
    manual: "Elle", rotation: "Rotasyon", employee_default: "Varsayılan",
    calendar_off: "Takvim", pre_hire: "Giriş öncesi", post_termination: "Ayrılış sonrası", unassigned: "Plan yok",
  };
  const qc = trpc.useUtils();
  const asgQ = trpc.hr.shiftPlan.assignmentList.useQuery({ employeeId }, { staleTime: 10_000 });
  const [delAsg, setDelAsg] = useState<{ id: string; label: string } | null>(null);
  const [defShift, setDefShift] = useState<string | null>(employeeDefaultShiftId);

  const removeAsg = trpc.hr.shiftPlan.assignmentRemove.useMutation({
    onSuccess: () => { setDelAsg(null); qc.hr.shiftPlan.invalidate(); pushToast({ tone: "success", message: "Atama kaldırıldı." }); },
    onError: (e) => { setDelAsg(null); pushToast({ tone: "danger", title: "Atama kaldırılamadı", message: e.message }); },
  });
  const setDefMut = trpc.hr.shiftPlan.setDefaultShift.useMutation({
    onSuccess: () => { qc.hr.shiftPlan.invalidate(); pushToast({ tone: "success", message: "Varsayılan vardiya güncellendi." }); },
    onError: (e) => { setDefShift(employeeDefaultShiftId); pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message }); },
  });

  const defOpts = useMemo<SSOption[]>(
    () => shifts.filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.name, tail: `${s.startTime}–${s.endTime}` })),
    [shifts],
  );

  return (
    <Drawer open onClose={onClose} size="md" title={employeeName} subtitle="Aylık vardiya planı">
      {/* ATAMA YÖNETİMİ — yanlış toplu atamanın tek geri dönüş yolu.
          Bu olmadan hatalı bir offset kalıcı hâle geliyordu (yeni atama çakışma hatası verir). */}
      {canManage ? (
        <>
          <div className="form-field">
            <span className="lbl">Rotasyon atamaları</span>
            {asgQ.isLoading ? <div className="mt-empty">Yükleniyor…</div>
              : (asgQ.data ?? []).length === 0 ? (
                <div className="shp-cur">Rotasyon ataması yok — plan varsayılan vardiyadan gelir.</div>
              ) : (
                <div className="shp-box shp-box-sm">
                  <table className="shp-tbl">
                    <thead><tr><th>Şablon</th><th>Kayma</th><th>Başlangıç</th><th>Bitiş</th><th className="w-row" /></tr></thead>
                    <tbody>
                      {(asgQ.data ?? []).map((a) => (
                        <tr key={a.id}>
                          <td>{a.patternName}<small className="shp-reason">{a.stepUnit === "week" ? "Haftalık" : "Günlük"} · {a.cycleLength} slot</small></td>
                          <td className="num">{a.cycleOffset}</td>
                          <td className="num">{String(a.startDate).slice(0, 10)}</td>
                          <td className="num">{a.endDate ? String(a.endDate).slice(0, 10) : "açık"}</td>
                          <td>
                            <button type="button" className="iact danger" title="Atamayı kaldır"
                              aria-label={`${a.patternName} atamasını kaldır`}
                              onClick={() => setDelAsg({ id: a.id, label: `${a.patternName} (${String(a.startDate).slice(0, 10)})` })}>
                              <Icon name="trash" size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>

          <div className="form-field">
            <span className="lbl">Varsayılan vardiya</span>
            <SearchSelect value={defShift} options={defOpts}
              onChange={(v) => { setDefShift(v); setDefMut.mutate({ employeeId, shiftId: v }); }}
              placeholder="Yok — rotasyonsuz günler istirahat" searchPlaceholder="Vardiya ara…" emptyText="Aktif vardiya yok" />
            <span className="help">Rotasyon atamasının kapsamadığı günlerde uygulanır (büro personeli). Rotasyon her zaman önceliklidir.</span>
          </div>

          <ConfirmDialog
            open={!!delAsg}
            title="Atama kaldırılsın mı?"
            message={<>{delAsg?.label} kaldırılacak. <b>Yayınlanmış geçmiş günler silinmez</b> — yalnız ileriye dönük hesaplama değişir.</>}
            tone="danger" confirmLabel="Kaldır" cancelLabel="Vazgeç" busy={removeAsg.isPending}
            onCancel={() => setDelAsg(null)}
            onConfirm={() => delAsg && removeAsg.mutate({ id: delAsg.id })}
          />
        </>
      ) : null}

      <span className="lbl">Gün dökümü</span>
      <div className="shp-box">
        <table className="shp-tbl">
          <thead>
            <tr><th className="w-row">Gün</th><th>Durum</th><th>Vardiya</th><th className="shp-c-src">Kaynak</th>{canManage ? <th className="w-row" /> : null}</tr>
          </thead>
          <tbody>
            {days.map((d) => {
              const l = dayLabel(d.date);
              return (
                <tr key={d.date}>
                  <td className="num">{l.d} {l.dow}</td>
                  <td>
                    {d.kind === "work" ? "Çalışma" : d.source === "calendar_off" ? "Tatil" : d.source === "unassigned" ? "Plan yok" : "İstirahat"}
                    {/* Dar ekranda Kaynak kolonu gizlenir; kaynak+uyarı+gerekçe bilgisi kaybolmasın diye buraya iner. */}
                    <small className="shp-reason shp-only-m">
                      {[SOURCE_LABEL[d.source] ?? d.source, ...d.warnings.map((w) => WARN_SHORT[w] ?? w), d.reason].filter(Boolean).join(" · ")}
                    </small>
                  </td>
                  {/* Saat aralığı AYRI satırda: "2. Vardiya (20:00–08:00)" dar ekranda 4 satıra kırılıyordu. */}
                  <td className="shp-c-shift">{d.shiftId ? (
                    <>{shiftName.get(d.shiftId) ?? "Vardiya"}<small className="shp-reason mono">{shiftTime.get(d.shiftId) ?? ""}</small></>
                  ) : "—"}</td>
                  <td className="shp-c-src">
                    {SOURCE_LABEL[d.source] ?? d.source}
                    {d.warnings.length ? ` · ${d.warnings.map((w) => WARN_SHORT[w] ?? w).join(", ")}` : ""}
                    {d.reason ? <small className="shp-reason">{d.reason}</small> : null}
                  </td>
                  {canManage ? (
                    <td>
                      <button type="button" className="iact" title="Bu günü düzenle" aria-label={`${l.long} gününü düzenle`} onClick={() => onEditDay(d.date)}>
                        <Icon name="edit" size={14} />
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Drawer>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Şablonlar
// ════════════════════════════════════════════════════════════════════════════

interface SlotDraft { slotIndex: number; kind: "work" | "off"; shiftId: string | null; label: string }

function PatternsDrawer({
  shifts, onClose, pushToast,
}: {
  // `code` = Ayarlar'daki KISA AD; rotasyon sırası önizlemesinde tam ad yerine bu gösterilir
  // (3-4 slotluk dizide "1. Vardiya → 2. Vardiya → 3. Vardiya" okunmaz olurdu).
  shifts: Array<{ id: string; name: string; code: string | null; startTime: string; endTime: string; isActive: boolean }>;
  onClose: () => void; pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const qc = trpc.useUtils();
  const listQ = trpc.hr.shiftPlan.patternList.useQuery({ includeInactive: true }, { staleTime: 5_000 });
  const [mode, setMode] = useState<{ kind: "list" } | { kind: "form"; id: string | null }>({ kind: "list" });
  const [delTarget, setDelTarget] = useState<{ id: string; name: string } | null>(null);

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [stepUnit, setStepUnit] = useState<"day" | "week">("week");
  const [cycleLength, setCycleLength] = useState(3);
  const [anchorDate, setAnchorDate] = useState("");
  const [respectCalendar, setRespectCalendar] = useState(true);
  // HAFTALIK İZİN GÜNLERİ (ISO 1=Pzt..7=Paz). Haftalık rotasyonda bir slot BÜTÜN haftayı kapsar,
  // dolayısıyla "haftanın hangi günü izin" slotlarla ifade EDİLEMEZ — bu yüzden ayrı alan.
  const [weeklyOff, setWeeklyOff] = useState<number[]>([]);
  const [isActive, setIsActive] = useState(true);
  /**
   * TAMPON — hiçbir zaman kısaltılmaz. Döngü uzunluğu düşerse fazla slotlar burada BEKLER,
   * ekrana `cycleLength` kadarı çizilir, sunucuya o kadarı gider. Aksi hâlde "21"i düzeltmek
   * için Backspace'e basan kullanıcı ("21"→"2") 19 satır vardiya+etiketi geri dönüşsüz kaybediyordu.
   */
  const [slotBuf, setSlotBuf] = useState<SlotDraft[]>([]);
  const slots = useMemo(
    () => Array.from({ length: cycleLength }, (_, i) => slotBuf[i] ?? { slotIndex: i, kind: "work" as const, shiftId: null, label: "" }),
    [slotBuf, cycleLength],
  );
  const setSlots = (fn: (prev: SlotDraft[]) => SlotDraft[]) =>
    setSlotBuf((buf) => {
      const view = Array.from({ length: cycleLength }, (_, i) => buf[i] ?? { slotIndex: i, kind: "work" as const, shiftId: null, label: "" });
      return [...fn(view), ...buf.slice(cycleLength)];
    });

  // Pasif vardiya seçenekten düşer ama KAYITLI değerse seçim boş görünüp sessizce geri yazılırdı
  // → mevcut seçim her zaman listede, "pasif" etiketiyle.
  const shiftOptsFor = (current: string | null): SSOption[] => {
    const list: SSOption[] = shifts.filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.name, tail: `${s.startTime}–${s.endTime}` }));
    if (current && !list.some((o) => o.value === current)) {
      const s = shifts.find((x) => x.id === current);
      if (s) list.unshift({ value: s.id, label: s.name, desc: "pasif vardiya", tail: `${s.startTime}–${s.endTime}` });
    }
    return list;
  };

  const openNew = () => {
    setName(""); setCode(""); setStepUnit("week"); setCycleLength(3);
    setAnchorDate(todayLocalIso()); setRespectCalendar(true); setIsActive(true);
    setWeeklyOff([]);
    setSlotBuf([]); setMode({ kind: "form", id: null });
  };
  const openEdit = (p: NonNullable<typeof listQ.data>[number]) => {
    setName(p.name); setCode(p.code ?? ""); setStepUnit(p.stepUnit as "day" | "week");
    setCycleLength(p.cycleLength); setAnchorDate(String(p.anchorDate).slice(0, 10));
    setRespectCalendar(p.respectCalendar); setIsActive(p.isActive);
    setWeeklyOff(p.weeklyOffDays ?? []);
    setSlotBuf(Array.from({ length: p.cycleLength }, (_, i) => {
      const s = p.slots.find((x) => x.slotIndex === i);
      return { slotIndex: i, kind: (s?.kind ?? "work") as "work" | "off", shiftId: s?.shiftId ?? null, label: s?.label ?? "" };
    }));
    setMode({ kind: "form", id: p.id });
  };
  // Yalnız uzunluk değişir; tampona DOKUNULMAZ (yukarıdaki not).
  const changeCycle = (n: number) => setCycleLength(Math.max(1, Math.min(366, n || 1)));

  const done = () => { qc.hr.shiftPlan.invalidate(); setMode({ kind: "list" }); };
  const createMut = trpc.hr.shiftPlan.patternCreate.useMutation({
    onSuccess: () => { pushToast({ tone: "success", message: "Şablon oluşturuldu." }); done(); },
    onError: (e) => pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message }),
  });
  const updateMut = trpc.hr.shiftPlan.patternUpdate.useMutation({
    onSuccess: () => { pushToast({ tone: "success", message: "Şablon güncellendi." }); done(); },
    onError: (e) => pushToast({ tone: "danger", title: "Kaydedilemedi", message: e.message }),
  });
  const removeMut = trpc.hr.shiftPlan.patternRemove.useMutation({
    onSuccess: () => { pushToast({ tone: "success", message: "Şablon silindi." }); setDelTarget(null); qc.hr.shiftPlan.invalidate(); },
    onError: (e) => { setDelTarget(null); pushToast({ tone: "danger", title: "Şablon silinemedi", message: `${e.message} Alternatif: şablonu pasife alın.` }); },
  });

  const badSlot = slots.findIndex((s) => s.kind === "work" && !s.shiftId);
  const formValid = name.trim().length > 0 && !!anchorDate && badSlot === -1;
  const busy = createMut.isPending || updateMut.isPending;

  const submit = () => {
    // slots DAİMA gönderilir: cycleLength değişip slots gitmezse sunucu 400 atar.
    const payload = {
      name: name.trim(), code: code.trim() || null, stepUnit, cycleLength,
      anchorDate, respectCalendar, weeklyOffDays: weeklyOff,
      // Tampon uzun olabilir; sunucuya YALNIZ görünür döngü kadarı gider (slotIndex 0..cycleLength-1).
      slots: slots.map((s, i) => ({ slotIndex: i, kind: s.kind, shiftId: s.kind === "work" ? s.shiftId : null, label: s.label.trim() || null })),
    };
    // `isActive` YALNIZ update şemasında var (create daima aktif açar).
    if (mode.kind === "form" && mode.id) updateMut.mutate({ id: mode.id, ...payload, isActive });
    else createMut.mutate(payload);
  };

  return (
    <Drawer
      open onClose={onClose} size="lg" dismissible={false}
      title={mode.kind === "list" ? "Vardiya Şablonları" : mode.id ? "Şablonu Düzenle" : "Yeni Şablon"}
      subtitle={mode.kind === "list" ? "Rotasyon desenleri" : "Döngü uzunluğu kadar slot tanımlayın"}
      footer={
        mode.kind === "list" ? (
          <>
            <button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>
            <button type="button" className="btn-md primary" onClick={openNew}><Icon name="plus" size={14} />Yeni Şablon</button>
          </>
        ) : (
          <>
            <button type="button" className="btn-md ghost" onClick={() => setMode({ kind: "list" })} disabled={busy}>Geri</button>
            <button type="button" className="btn-md primary" disabled={!formValid || busy} onClick={submit}>
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </>
        )
      }
    >
      {mode.kind === "list" ? (
        listQ.isLoading ? <div className="mt-empty">Yükleniyor…</div>
        : (listQ.data ?? []).length === 0 ? (
          <div className="empty-state">
            <span className="ic" aria-hidden="true"><Icon name="list2" size={30} /></span>
            <p className="ttl">Şablon yok</p>
            <p className="sub">"Yeni Şablon" ile bir rotasyon deseni tanımlayın (örn. 3 vardiya haftalık dönüşüm).</p>
          </div>
        ) : (
          <div className="shp-box">
            <table className="shp-tbl">
              <thead><tr><th>Ad</th><th>Adım</th><th>Döngü</th><th>Çapa</th><th>Atama</th><th>Durum</th><th className="w-row" /></tr></thead>
              <tbody>
                {(listQ.data ?? []).map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.name}
                      {!p.isComplete ? <span className="shp-warntag shp-gap"><Icon name="alertTriangle" size={10} />Desen eksik</span> : null}
                    </td>
                    <td>{p.stepUnit === "week" ? "Haftalık" : "Günlük"}</td>
                    <td className="num">{p.cycleLength}</td>
                    <td className="num">{String(p.anchorDate).slice(0, 10)}</td>
                    <td className="num">{p.assignmentCount}</td>
                    <td><span className={`badge-pill ${p.isActive ? "b-success" : ""}`}><Icon name={p.isActive ? "check" : "x"} size={11} />{p.isActive ? "Aktif" : "Pasif"}</span></td>
                    <td>
                      <span className="row-acts">
                        <button type="button" className="iact" title="Düzenle" aria-label={`${p.name}: düzenle`} onClick={() => openEdit(p)}><Icon name="edit" size={14} /></button>
                        <button type="button" className="iact danger" title="Sil" aria-label={`${p.name}: sil`} onClick={() => setDelTarget({ id: p.id, name: p.name })}><Icon name="trash" size={14} /></button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <>
          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor="shp-pname">Ad <span className="req">*</span></label>
              <input id="shp-pname" className="form-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="shp-pcode">Kod</label>
              <input id="shp-pcode" className="form-input mono" value={code} onChange={(e) => setCode(e.target.value)} maxLength={30} placeholder="3V-HAFTALIK" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <span className="lbl">Adım birimi</span>
              <div className="seg shp-seg">
                <button type="button" className={`seg-btn${stepUnit === "week" ? " active" : ""}`} onClick={() => setStepUnit("week")}>Hafta</button>
                <button type="button" className={`seg-btn${stepUnit === "day" ? " active" : ""}`} onClick={() => setStepUnit("day")}>Gün</button>
              </div>
              <span className="help">Haftalıkta slot her hafta, günlükte her takvim günü ilerler.</span>
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="shp-cycle">Döngü uzunluğu <span className="req">*</span></label>
              <input id="shp-cycle" className="form-input mono" type="number" min={1} max={366} value={cycleLength}
                onChange={(e) => changeCycle(Number(e.target.value))} />
              <span className="help">Kaç adımda bir tekrar etsin (3 vardiya için 3).</span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor="shp-anchor">Çapa tarihi <span className="req">*</span></label>
              <input id="shp-anchor" className="form-input" type="date" value={anchorDate} onChange={(e) => setAnchorDate(e.target.value)} />
              <span className="help">Desenin 0. slotunun düştüğü referans tarih — atamanın başlangıç tarihi değildir.</span>
            </div>
            <div className="form-field">
              <span className="lbl">Takvime uy</span>
              <label className="form-checkbox">
                <input type="checkbox" checked={respectCalendar} onChange={(e) => setRespectCalendar(e.target.checked)} />
                <span>Hafta tatili ve resmî tatillerde çalışma yazma</span>
              </label>
              <span className="help">Kapalıysa kesintisiz üretim düzeni (7/24) uygulanır.</span>
            </div>
          </div>

          {/* HAFTALIK İZİN GÜNÜ — haftalık rotasyonda slot bütün haftayı kapsadığı için
              "hangi gün izin" slotlarla söylenemez; bu yüzden ayrı. Tesis takviminden FARKLI:
              o herkese aynı tatili verir, bu ŞABLONA özeldir (A ekibi Pazar, B ekibi Çarşamba). */}
          <div className="form-field">
            <span className="lbl">Haftalık izin günü</span>
            <div className="shp-quick" role="group" aria-label="Haftalık izin günleri">
              {[1, 2, 3, 4, 5, 6, 7].map((iso) => {
                const on = weeklyOff.includes(iso);
                return (
                  <button
                    key={iso} type="button"
                    className={`btn-md ${on ? "primary" : "ghost"} sm`}
                    aria-pressed={on}
                    onClick={() => setWeeklyOff((prev) => (prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso].sort((a, b) => a - b)))}
                  >
                    {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"][iso - 1]}
                  </button>
                );
              })}
            </div>
            <span className="help">
              {weeklyOff.length === 0
                ? "Seçilmezse haftalık izin uygulanmaz; izin yalnız slot “istirahat” ise ya da takvim tatilinde oluşur."
                : "Bu günler slot ne derse desin istirahat olur — bu şablonu kullanan herkes için."}
            </span>
          </div>

          {/* Pasife alma: atanmış şablon SİLİNEMEZ (denetim izi), tek çıkış yolu budur. */}
          {mode.id ? (
            <div className="form-field">
              <span className="lbl">Durum</span>
              <label className="form-checkbox">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                <span>Aktif — yeni atamalarda seçilebilir</span>
              </label>
              <span className="help">Pasif şablon yeni personele atanamaz; mevcut atamalar ve geçmiş plan aynen durur.</span>
            </div>
          ) : null}

          {/* ROTASYON SIRASI ÖNİZLEMESİ — "A-B-C mi B-C-A mı?" sorusunun cevabı slot SIRASIDIR;
              ayrı bir alan gerekmez. Sıra görünmediği için kullanıcı bunu bilemiyordu. */}
          <div className="form-field">
            <span className="lbl">Rotasyon sırası</span>
            <div className="shp-seq" aria-live="polite">
              {slots.map((s, i) => {
                const sh = s.shiftId ? shifts.find((x) => x.id === s.shiftId) : null;
                return (
                  <span key={i} className="shp-seq-i">
                    {i > 0 ? <Icon name="arrowRight" size={12} /> : null}
                    <span className={`badge-pill${s.kind === "off" ? "" : " b-primary"}`}>
                      {s.kind === "off" ? "İstirahat" : sh ? (sh.code || sh.name) : "?"}
                    </span>
                  </span>
                );
              })}
            </div>
            <span className="help">
              {stepUnit === "week" ? "Her adım bir HAFTA" : "Her adım bir GÜN"} — bu dizi baştan sona
              döner. Sırayı değiştirmek için aşağıdaki slotlardaki vardiyaları yeniden seçin
              (örn. Gündüz→Gece→Ara yerine Gündüz→Ara→Gece).
              <button
                type="button" className="btn-md ghost sm"
                onClick={() => setSlots((prev) => [prev[0]!, ...prev.slice(1).reverse()])}
                title="İlk adım sabit kalır, kalan sıra ters çevrilir"
              >
                <Icon name="shuffle" size={13} />Sırayı ters çevir
              </button>
            </span>
          </div>

          <div className="form-field">
            <span className="lbl">Desen slotları</span>
            <div className="shp-quick">
              <button type="button" className="shp-linkbtn" onClick={() => setSlots((p) => p.map((s) => ({ ...s, kind: "work" })))}>Hepsini çalışma yap</button>
              <button type="button" className="shp-linkbtn" onClick={() => setSlots((p) => p.map((s) => ({ ...s, kind: "off", shiftId: null })))}>Hepsini istirahat yap</button>
            </div>
            {slots.map((s, i) => (
              <div key={s.slotIndex} className={`shp-slot-row${s.kind === "work" && !s.shiftId ? " err" : ""}`}>
                <span className="idx">{s.slotIndex}</span>
                <div className="seg shp-seg">
                  <button type="button" className={`seg-btn${s.kind === "work" ? " active" : ""}`}
                    onClick={() => setSlots((p) => p.map((x, j) => j === i ? { ...x, kind: "work" } : x))}>Çalışma</button>
                  <button type="button" className={`seg-btn${s.kind === "off" ? " active" : ""}`}
                    onClick={() => setSlots((p) => p.map((x, j) => j === i ? { ...x, kind: "off", shiftId: null } : x))}>İstirahat</button>
                </div>
                <SearchSelect value={s.shiftId} options={shiftOptsFor(s.shiftId)} disabled={s.kind === "off"}
                  onChange={(v) => setSlots((p) => p.map((x, j) => j === i ? { ...x, shiftId: v } : x))}
                  placeholder={s.kind === "off" ? "—" : "Vardiya seçin…"} searchPlaceholder="Vardiya ara…" />
                <input className="form-input" value={s.label} maxLength={60} placeholder="Etiket (örn. A ekibi)"
                  onChange={(e) => setSlots((p) => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
              </div>
            ))}
            {badSlot >= 0 ? <div className="form-banner-err" role="alert">Slot {badSlot} çalışma günü — vardiya seçilmeli.</div> : null}
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!delTarget}
        title={`"${delTarget?.name}" şablonu silinsin mi?`}
        message="Şablon herhangi bir personele atanmışsa ya da geçmiş plan satırlarında kullanılmışsa silinemez; bu durumda pasife alabilirsiniz."
        tone="danger" confirmLabel="Sil" cancelLabel="Vazgeç" busy={removeMut.isPending}
        onCancel={() => setDelTarget(null)}
        onConfirm={() => delTarget && removeMut.mutate({ id: delTarget.id })}
      />
    </Drawer>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Toplu ekip ataması
// ════════════════════════════════════════════════════════════════════════════

function BulkAssignDrawer({
  employees, onClose, onDone, pushToast,
}: {
  employees: Array<{ id: string; code: string | null; name: string; positionTitle: string | null }>;
  onClose: () => void; onDone: () => void; pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const patternsQ = trpc.hr.shiftPlan.patternList.useQuery(undefined, { staleTime: 60_000 });
  const [patternId, setPatternId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(todayLocalIso());
  const [endDate, setEndDate] = useState("");
  const [closePrevious, setClosePrevious] = useState(true);
  const [selected, setSelected] = useState<Map<string, number>>(new Map());

  const pattern = (patternsQ.data ?? []).find((p) => p.id === patternId) ?? null;
  // Döngüden büyük kayma anlamsız (mod ile aynı sonuca düşer) ve sunucu sınırı 365.
  const offsetMax = Math.min(365, Math.max(0, (pattern?.cycleLength ?? 366) - 1));
  const patternOpts = useMemo<SSOption[]>(
    () => (patternsQ.data ?? []).map((p) => ({ value: p.id, label: p.name, tail: `${p.stepUnit === "week" ? "Haftalık" : "Günlük"} · ${p.cycleLength}` })),
    [patternsQ.data],
  );

  const toggle = (id: string) => setSelected((prev) => {
    const n = new Map(prev);
    if (n.has(id)) n.delete(id); else n.set(id, 0);
    return n;
  });
  // TABLO sırasına göre dağıt. `[...prev.keys()]` Map'in EKLEME sırasıdır: bir personelin
  // seçimi kaldırılıp yeniden işaretlenirse listenin sonuna düşer ve tüm ekipler kayardı.
  const spread = () => setSelected((prev) => {
    const n = new Map<string, number>();
    const len = pattern?.cycleLength ?? 1;
    employees.filter((e) => prev.has(e.id)).forEach((e, i) => n.set(e.id, i % len));
    return n;
  });

  const mut = trpc.hr.shiftPlan.assignmentBulk.useMutation({
    onSuccess: (r) => {
      pushToast({
        tone: r.skipped.length ? "warning" : "success",
        title: `${r.created} atama oluşturuldu`,
        message: r.skipped.length ? `${r.skipped.length} personel atlandı:` : undefined,
        lines: r.skipped.map((s) => `${s.code ?? s.employeeId}: ${s.reason}`),
        duration: r.skipped.length ? 0 : undefined,
      });
      onDone();
    },
    onError: (e) => pushToast({ tone: "danger", title: "Toplu atama başarısız", message: e.message }),
  });

  const overLimit = selected.size > 500;
  const valid = !!patternId && !!startDate && selected.size > 0 && !overLimit;

  return (
    <Drawer
      open onClose={onClose} size="lg" dismissible={false}
      title="Toplu Ekip Ataması" subtitle={`${selected.size} personel seçili`}
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={mut.isPending}>Vazgeç</button>
          <button type="button" className="btn-md primary" disabled={!valid || mut.isPending}
            onClick={() => mut.mutate({
              patternId: patternId!, startDate, endDate: endDate || null, closePrevious,
              // Şablon sonradan değişmiş olabilir → gönderimde de kırp (zod reddi tüm partiyi düşürür).
              entries: [...selected.entries()].map(([employeeId, cycleOffset]) => ({ employeeId, cycleOffset: Math.min(offsetMax, Math.max(0, cycleOffset)) })),
            })}>
            {mut.isPending ? "Atanıyor…" : `Ata (${selected.size})`}
          </button>
        </>
      }
    >
      <div className="form-field">
        <span className="lbl">Şablon <span className="req">*</span></span>
        <SearchSelect value={patternId} options={patternOpts} onChange={setPatternId}
          placeholder="Rotasyon şablonu seçin…" searchPlaceholder="Şablon ara…" emptyText="Aktif şablon yok" />
      </div>
      {pattern ? (
        <div className="form-banner-info">
          <Icon name="info" size={14} className="hr-bnr-ic" />
          <div>
            <b>{pattern.stepUnit === "week" ? "Haftalık" : "Günlük"}</b> · {pattern.cycleLength} slotluk döngü · Çapa {String(pattern.anchorDate).slice(0, 10)}.
            Kayma (offset) birimi <b>{pattern.stepUnit === "week" ? "hafta" : "gün"}</b>: A ekibi 0, B ekibi 1, C ekibi 2.
          </div>
        </div>
      ) : null}

      <div className="form-row">
        <div className="form-field">
          <label className="lbl" htmlFor="shp-bstart">Başlangıç <span className="req">*</span></label>
          <input id="shp-bstart" className="form-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="shp-bend">Bitiş</label>
          <input id="shp-bend" className="form-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <span className="help">Boş bırakılırsa açık uçlu.</span>
        </div>
      </div>

      <div className="form-field">
        <label className="form-checkbox">
          <input type="checkbox" checked={closePrevious} onChange={(e) => setClosePrevious(e.target.checked)} />
          <span>Önceki açık uçlu atamayı kapat</span>
        </label>
        <span className="help">Personelin önceki ataması, yeni başlangıçtan bir gün önce sonlandırılır.</span>
      </div>

      <div className="hr-bulk" role="status" aria-live="polite">
        <span className="hr-bulk-n"><b className="mono">{selected.size}</b> personel seçili</span>
        {overLimit ? <span className="hr-bulk-warn"><Icon name="alertTriangle" size={12} />Tek seferde en fazla 500 personel</span> : null}
        <button type="button" className="shp-linkbtn" onClick={() => setSelected(new Map(employees.map((e) => [e.id, 0])))}>Tümünü seç</button>
        <button type="button" className="shp-linkbtn" onClick={spread} disabled={!pattern}>Sırayla dağıt (A=0, B=1…)</button>
        <button type="button" className="hr-bulk-clear" onClick={() => setSelected(new Map())}><Icon name="x" size={12} />Temizle</button>
      </div>

      <div className="shp-box">
        <table className="shp-tbl">
          <thead><tr><th className="w-row" /><th>Personel</th><th>Pozisyon</th><th className="w-name">Kayma</th></tr></thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.id}>
                <td className="hr-chk-col" onClick={(ev) => ev.stopPropagation()}>
                  <input type="checkbox" aria-label={`${e.name} seç`} checked={selected.has(e.id)} onChange={() => toggle(e.id)} />
                </td>
                <td>{e.name}</td>
                <td>{e.positionTitle ?? "—"}</td>
                <td>
                  {/* Üst sınır ŞART: tek geçersiz değer (örn. 400) zod'u patlatıp 200 kişilik
                      partinin TAMAMINI iptal ediyordu. HTML max= yazmayı/yapıştırmayı engellemez. */}
                  <input className="form-input mono shp-off-inp" type="number" min={0} max={offsetMax}
                    disabled={!selected.has(e.id)} value={selected.get(e.id) ?? 0}
                    aria-label={`${e.name} kayma değeri`}
                    onChange={(ev) => setSelected((p) => {
                      const n = new Map(p);
                      n.set(e.id, Math.min(offsetMax, Math.max(0, Math.trunc(Number(ev.target.value)) || 0)));
                      return n;
                    })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Drawer>
  );
}
